/**
 * App.Command - 撤销/重做：前后快照
 *
 * 网格小（26×18），采用 structuredClone 前后快照：实现简单、无差分还原 bug。
 * · do(label, mutate)：执行仅改 Store 的函数，自动包装为一条可撤销命令
 * · 拖拽：beginDrag 记起点快照 → 移动中调用方实时 mutate+render → endDrag 提交一条命令（整段拖动=一次撤销）
 */

import type { SceneSnapshot } from '../types';
import { App } from './App';

/* ─────────────── Module: Command（撤销/重做：前后快照）─────────────── */
App.Command = {
  /** 撤销栈 */
  undoStack: [] as Array<{ label: string; before: SceneSnapshot; after: SceneSnapshot }>,

  /** 重做栈 */
  redoStack: [] as Array<{ label: string; before: SceneSnapshot; after: SceneSnapshot }>,

  /** 最大栈大小 */
  maxSize: 80,

  /** 拖拽前快照 */
  dragBefore: null as SceneSnapshot | null,

  /**
   * 执行命令（可撤销）
   * @param label 命令标签
   * @param mutate 状态变更函数
   */
  do(label: string, mutate: () => void): boolean {
    if (App.Store.running) return false;

    const before = this.snapshot();
    let ok = true;

    try {
      mutate();
    } catch (e) {
      ok = false;
    }

    if (!ok) {
      this.restore(before);
      return false;
    }

    const after = this.snapshot();
    this.undoStack.push({ label, before, after });

    if (this.undoStack.length > this.maxSize) {
      this.undoStack.shift();
    }

    this.redoStack = [];
    this.afterChange();
    return true;
  },

  /**
   * 开始拖拽（记录起点快照）
   */
  beginDrag(): void {
    this.dragBefore = this.snapshot();
  },

  /**
   * 结束拖拽（提交一条命令）
   */
  endDrag(label: string): void {
    if (!this.dragBefore) return;

    const before = this.dragBefore;
    this.dragBefore = null;

    const after = this.snapshot();
    this.undoStack.push({ label, before, after });

    if (this.undoStack.length > this.maxSize) {
      this.undoStack.shift();
    }

    this.redoStack = [];
    this.afterChange();
  },

  /**
   * 取消拖拽
   */
  cancelDrag(): void {
    this.dragBefore = null;
  },

  /**
   * 撤销
   */
  undo(): boolean {
    if (App.Store.running || !this.undoStack.length) return false;

    const c = this.undoStack.pop()!;
    this.restore(c.before);
    this.redoStack.push(c);
    this.afterChange();

    App.UI.setProgress('撤销：' + c.label);
    return true;
  },

  /**
   * 重做
   */
  redo(): boolean {
    if (App.Store.running || !this.redoStack.length) return false;

    const c = this.redoStack.pop()!;
    this.restore(c.after);
    this.undoStack.push(c);
    this.afterChange();

    App.UI.setProgress('重做：' + c.label);
    return true;
  },

  /**
   * 创建场景快照
   */
  snapshot(): SceneSnapshot {
    const S = App.Store;
    return {
      layoutIdx: S.layoutIdx,
      bindMode: S.bindMode,
      activeFloorIdx: S.activeFloorIdx,
      floorCounter: S.floorCounter,
      roomCounter: S.roomCounter,
      endCounter: S.endCounter,
      shaftCounter: S.shaftCounter,
      systemCounter: S.systemCounter,
      activeSystemId: S.activeSystemId,
      cornerPenalty: S.cornerPenalty,
      curLine: S.curLine.slice(),
      floors: structuredClone(S.floors),
      shafts: structuredClone(S.shafts),
      systems: structuredClone(S.systems),
    };
  },

  /**
   * 恢复场景快照
   */
  restore(s: SceneSnapshot): void {
    const S = App.Store;

    S.layoutIdx = s.layoutIdx;
    S.bindMode = s.bindMode;
    S.activeFloorIdx = s.activeFloorIdx;
    S.floorCounter = s.floorCounter;
    S.roomCounter = s.roomCounter;
    S.endCounter = s.endCounter;
    S.shaftCounter = s.shaftCounter;
    S.systemCounter = s.systemCounter;
    S.activeSystemId = s.activeSystemId;
    S.cornerPenalty = s.cornerPenalty;
    S.curLine = s.curLine.slice();
    S.floors = structuredClone(s.floors);
    S.shafts = structuredClone(s.shafts);
    S.systems = structuredClone(s.systems);

    S.activeFloorIdx = Math.min(S.activeFloorIdx, S.floors.length - 1);
    S.f = S.floors[S.activeFloorIdx] || S.floors[0];

    const ln = document.getElementById('layoutName');
    if (ln) ln.textContent = App.Config.LAYOUTS[S.layoutIdx];

    const cb = document.getElementById('chkBind');
    if (cb) (cb as HTMLInputElement).checked = S.bindMode;

    const cp = document.getElementById('cornerPenalty');
    if (cp) (cp as HTMLInputElement).value = String(S.cornerPenalty);
  },

  /**
   * 命令执行后更新UI
   */
  afterChange(): void {
    const RR = App.Render;
    const UI = App.UI;
    const S = App.Store;

    RR.rebuildAll();
    RR.gPath.clear();
    RR.clearFlows();
    RR.gSearch.clear();
    S.results = [];

    UI.refreshMetrics(0);
    UI.updateDrawStatus();
    UI.renderBindUI();
    UI.renderFloorPanel();
    UI.renderFloorTabs();
    UI.renderSystemPanel();
    UI.renderRoomSystemPick();
    UI.renderEndPanel();
    UI.renderRenamePanel();
    UI.legend();
    UI.updatePlanBadge();
    UI.updateUndoRedoButtons();

    (App.Selection as any).afterChange();
  },

  /**
   * 清空撤销/重做栈
   */
  clear(): void {
    this.undoStack = [];
    this.redoStack = [];
    App.UI.updateUndoRedoButtons();
  },
};

export default App.Command;
export const Command = App.Command;
